import { useEffect, type Dispatch, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import {
  getFurnitureCatalogFurnitureCatalogGet,
  getListingForHouseMarketplaceListingMapObjectIdGet,
  getRateMarketplaceRateGet,
} from '../../client';
import { fetchHouseFurniture } from '../../scenes/room/state/layoutSync';
import type { useCreateUserAction } from '../../hooks/useCreateUserAction';
import type { MarketplaceListing, MarketplaceRate } from '../../models/apiTypes';
import type { MapObject } from '../../models/mapObject';
import type { OwnerMessageTranslationState } from './types';

export interface UseRealEstateOpenTimeParams {
  isOpen: boolean;
  openTimeRef: { current: number };
}

export function useRealEstateOpenTime({
  isOpen,
  openTimeRef,
}: UseRealEstateOpenTimeParams): void {
  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = Date.now();
    }
  }, [isOpen, openTimeRef]);
}

export interface UseRealEstateTranslationResetParams {
  plot: MapObject | null;
  setTranslation: Dispatch<SetStateAction<OwnerMessageTranslationState>>;
}

export function useRealEstateTranslationReset({
  plot,
  setTranslation,
}: UseRealEstateTranslationResetParams): void {
  useEffect(() => {
    // Prior code cleared message/showing but left isTranslating alone — keep loading mid-flight.
    setTranslation((prev) => (prev.status === 'loading' ? prev : { status: 'idle' }));
  }, [plot?.id, setTranslation]);
}

export interface UseRealEstateVisitTrackingParams {
  isOpen: boolean;
  plot: MapObject | null;
  viewRecorded: boolean;
  currentUserId: string | undefined;
  createUserAction: ReturnType<typeof useCreateUserAction>['createUserAction'];
  setViewRecorded: Dispatch<SetStateAction<boolean>>;
  setBuyLoading: Dispatch<SetStateAction<boolean>>;
  setBuyListing: Dispatch<SetStateAction<MarketplaceListing | null>>;
  setBuyRate: Dispatch<SetStateAction<MarketplaceRate | null>>;
  setSellPriceTon: Dispatch<SetStateAction<string>>;
  setSellPriceUsdt: Dispatch<SetStateAction<string>>;
}

export function useRealEstateVisitTracking({
  isOpen,
  plot,
  viewRecorded,
  currentUserId,
  createUserAction,
  setViewRecorded,
  setBuyLoading,
  setBuyListing,
  setBuyRate,
  setSellPriceTon,
  setSellPriceUsdt,
}: UseRealEstateVisitTrackingParams): void {
  // Додати перегляд будинку при відкритті (тільки для гостей, не для власника)
  // Важливо: цей useEffect має бути перед умовним return, щоб не порушувати правила Hooks
  useEffect(() => {
    if (!isOpen || !plot || viewRecorded) return;
    if (plot.type !== 'house' || !plot.data?.real_estate_id || !plot.owner_id) return;
    
    const owner = plot.owner;
    const isOwner = currentUserId && owner && String(currentUserId) === String(owner.user_id);
    
    // Записуємо перегляд тільки якщо користувач не є власником і будинок у власності
    if (!isOwner) {
      createUserAction('visit_player_house', plot.id).catch((error) => {
        console.error("[RealEstateInfoModal] Failed to record view:", error);
      });
      setViewRecorded(true);
      // Fetch marketplace listing for non-owners
      setBuyLoading(true);
      Promise.all([
        (async () => {
          const { data, response } = await getListingForHouseMarketplaceListingMapObjectIdGet({
            path: { map_object_id: plot.id },
            throwOnError: false,
          });
          return response?.status === 200 ? (data ?? null) : null;
        })().catch(() => null),
        (async () => {
          const { data } = await getRateMarketplaceRateGet({ throwOnError: true });
          return data;
        })().catch(() => null),
      ]).then(([listing, rate]) => {
        setBuyListing(listing);
        setBuyRate(rate);
        if (listing) {
          posthog.capture('marketplace_listing_viewed', {
            listing_id: listing.id,
            house_id: listing.map_object_id,
            real_estate_id: plot.data?.real_estate_id,
            price_usdt: listing.price_usdt,
            price_ton: listing.price_ton,
            seller_id: listing.seller_id,
          });
        }
      }).finally(() => setBuyLoading(false));
    }
    // Свідомо: plot.id / type / owner_id / real_estate_id — не цілий plot (перестворюється
    // з mapObjects snapshot). createUserAction з useCreateUserAction() — нова функція щорендеру.
    // set* — стабільні setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- див. коментар вище
  }, [
    isOpen,
    plot?.id,
    plot?.type,
    plot?.owner_id,
    plot?.data?.real_estate_id,
    currentUserId,
    viewRecorded,
    setViewRecorded,
    setBuyLoading,
    setBuyListing,
    setBuyRate,
  ]);
  
  // Скидаємо прапорець перегляду та buy state при закритті модального вікна
  useEffect(() => {
    if (!isOpen) {
      setViewRecorded(false);
      setBuyListing(null);
      setBuyRate(null);
      setBuyLoading(true);
      setSellPriceTon('');
      setSellPriceUsdt('');
    }
  }, [isOpen, setBuyListing, setBuyLoading, setBuyRate, setSellPriceTon, setSellPriceUsdt, setViewRecorded]);
}

export interface UseHouseFurniturePrefetchParams {
  isOpen: boolean;
  plot: MapObject | null;
  queryClient: QueryClient;
}

export function useHouseFurniturePrefetch({
  isOpen,
  plot,
  queryClient,
}: UseHouseFurniturePrefetchParams): void {
  useEffect(() => {
    if (isOpen && plot && plot.type === 'house' && typeof plot.id === 'number') {
      queryClient.prefetchQuery({
        queryKey: ['houseFurniture', plot.id],
        queryFn: () => fetchHouseFurniture(plot.id),
      }).catch(() => {});
      if (!queryClient.getQueryData(['furnitureCatalog'])) {
        queryClient.prefetchQuery({
          queryKey: ['furnitureCatalog'],
          queryFn: async () => {
            const { data } = await getFurnitureCatalogFurnitureCatalogGet({ throwOnError: true });
            return data.items;
          },
        }).catch(() => {});
      }
    }
    // Свідомо: plot?.id + plot?.type (примітиви), не цілий plot — plot з mapObjects
    // перестворюється щоснапшот → зайві prefetchQuery. queryClient стабільний.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- див. коментар вище
  }, [isOpen, plot?.id, plot?.type, queryClient]);
}

export interface UseMirrorDirectionSyncParams {
  isOpen: boolean;
  plot: MapObject | null;
  currentUserId: string | undefined;
  setCurrentMirrorDirection: Dispatch<SetStateAction<"left" | "right" | null>>;
}

export function useMirrorDirectionSync({
  isOpen,
  plot,
  currentUserId,
  setCurrentMirrorDirection,
}: UseMirrorDirectionSyncParams): void {
  // Отримуємо поточний напрямок відзеркалення з plot.mirror_direction (тепер на верхньому рівні)
  // Важливо: цей useEffect має бути перед умовним return, щоб не порушувати правила Hooks
  useEffect(() => {
    if (isOpen && plot && plot.type === 'house' && plot.data?.real_estate_id) {
      const owner = plot.owner;
      const isOwnerCheck = currentUserId && owner && String(currentUserId) === String(owner.user_id);
      
      if (isOwnerCheck) {
        // Беремо напрямок відзеркалення з plot.mirror_direction (за замовчуванням "left")
        const mirrorDir = plot.mirror_direction || 'left';
        setCurrentMirrorDirection(mirrorDir as "left" | "right");
      } else {
        // Якщо не власник, скидаємо напрямок
        setCurrentMirrorDirection(null);
      }
    } else {
      // Якщо модалка закрита або це не будинок, скидаємо напрямок
      setCurrentMirrorDirection(null);
    }
  }, [isOpen, plot, currentUserId, setCurrentMirrorDirection]);
}
